// The template-field table: which strings of a step, check, condition or target are templates, and in which
// field. Every other string is data and is never parsed (template.ts lists them).
import { display, parse, refs, serialize, type Template, type TemplateField } from './template.ts'

export class DuplicateParameterError extends Error {}

/** `operands`: what tool binding substitutes. `all`: also the display text the runner renders. */
export type TemplateSelection = 'all' | 'operands'

const OPERAND_FIELDS: ReadonlyMap<string, TemplateField> = new Map([
  ['value', 'value'], ['expected', 'value'], ['recipient', 'value'], ['context', 'value'], ['pattern', 'value'],
  ['selector', 'selector'], ['init', 'toolInit'], ['property', 'target'], ['parameter', 'target'],
])
// Never bound by tools (C3); `checks` is a string check.
const DISPLAY_FIELDS: ReadonlyMap<string, TemplateField> = new Map([
  ['action', 'prose'], ['description', 'prose'], ['checks', 'prose'], ['containerSelector', 'selector'],
])

/** Map the source text of every selected template field, never observed page text. A type/input `value` is a fill.
 * Params whose mapped keys collide throw, or with `keep` stay in stored form. */
export function mapTemplateTexts<T>(value: T, map: (text: string, field: TemplateField) => string, select: TemplateSelection, collisions: 'throw' | 'keep' = 'throw'): T {
  const fieldOf = (key: string | undefined): TemplateField | undefined =>
    OPERAND_FIELDS.get(key ?? '') ?? (select === 'all' ? DISPLAY_FIELDS.get(key ?? '') : undefined)
  const mapParameters = (params: object, literal: boolean): Record<string, unknown> => {
    const entries = Object.entries(params).map(([key, text]) => [literal ? key : map(key, 'target'), visit(text, 'parameter', literal)] as const)
    if (new Set(entries.map(([key]) => key)).size === entries.length) return Object.fromEntries(entries)
    if (collisions === 'keep') return { ...params }
    throw new DuplicateParameterError('Tool variable bindings produce duplicate property names')
  }
  const mapArguments = (args: object): Record<string, unknown> =>
    Object.fromEntries(Object.entries(args).map(([name, text]) => [name, typeof text === 'string' ? map(text, 'argument') : text]))
  const visit = (item: unknown, key?: string, observed = false, fill = false): unknown => {
    if (typeof item === 'string') {
      const field = fieldOf(key)
      return !observed && field ? map(item, fill ? 'fill' : field) : item
    }
    if (Array.isArray(item)) return item.map(child => visit(child, key, observed))
    if (item && typeof item === 'object') {
      const record = item as Record<string, unknown>
      const literal = observed || record.paramsSource === 'observation'
      const fills = record.type === 'type' || record.type === 'input'
      return Object.fromEntries(Object.entries(record).map(([name, child]) => [name,
        name === 'params' && child && typeof child === 'object' ? mapParameters(child, literal)
          : name === 'arguments' && select === 'all' && child && typeof child === 'object' ? mapArguments(child)
            : visit(child, name, literal || name === 'selector' && typeof record.surfaceId === 'string' && Array.isArray(record.path), fills && name === 'value')]))
    }
    return item
  }
  return visit(value) as T
}

/** `mapTemplateTexts` over parsed templates. */
export function mapTemplateFields<T>(value: T, transform: (template: Template) => Template, select: TemplateSelection): T {
  return mapTemplateTexts(value, (text, field) => serialize(transform(parse(text, field))), select)
}

/** Referenced names in occurrence order, repeats kept. */
export function templateRefs(value: unknown, select: TemplateSelection): string[] {
  const names: string[] = []
  mapTemplateTexts(value, (text, field) => {
    names.push(...refs(parse(text, field)))
    return text
  }, select)
  return names
}

/** `value` with every template field in display form: equal for equal meaning in any grammar. Only for hashes and equality.
 * Params whose keys display alike (a reference and its literal) keep their stored form: compared unequal, never merged. */
export function templateIdentity<T>(value: T): T {
  return mapTemplateTexts(value, (text, field) => display(parse(text, field)), 'all', 'keep')
}
